import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

/**
 * Handles:
 * - Dynamic cost estimation when model/duration changes (new form)
 * - ActionCable subscription for generation progress (show page)
 * - UI state transitions: generating → completed / failed
 */
export default class extends Controller {
  static targets = [
    "prompt", "modelSelect", "durationSelect", "costValue", "costDisplay",
    "styleGrid", "generatingState", "completedState", "failedState",
    "statusMessage"
  ]

  static values = {
    estimateUrl: String,    // /ai_video_clips/estimate (new form)
    configId: Number,       // Video config ID (show page)
    channel: String,        // ActionCable channel name (show page)
    status: String          // Current status (show page)
  }

  connect() {
    if (this.hasEstimateUrlValue) {
      this.updateEstimate()
    }

    if (this.hasChannelValue && this.statusValue === "generating") {
      this.subscribeToChannel()
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  // --- Cost Estimation (new form) ---

  async updateEstimate() {
    if (!this.hasEstimateUrlValue) return

    const modelKey = this.hasModelSelectTarget ? this.modelSelectTarget.value : "veo_3_1_fast"
    const duration = this.hasDurationSelectTarget ? this.durationSelectTarget.value : "8"

    const url = new URL(this.estimateUrlValue, window.location.origin)
    url.searchParams.set("model_key", modelKey)
    url.searchParams.set("duration", duration)

    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })

      if (response.ok) {
        const data = await response.json()
        if (this.hasCostValueTarget) {
          this.costValueTarget.textContent = data.cost_display
        }
      }
    } catch {
      // Silently fail — the estimate is informational
    }
  }

  // --- ActionCable Progress (show page) ---

  subscribeToChannel() {
    const consumer = createConsumer()
    const controller = this

    this.subscription = consumer.subscriptions.create(
      { channel: "AiVideoClipChannel", config_id: this.configIdValue },
      {
        received(data) {
          controller.handleProgress(data)
        }
      }
    )
  }

  handleProgress(data) {
    if (data.status === "generating" && this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = data.message || "Generating..."
    }

    if (data.status === "completed") {
      // Reload the page to show the completed state with video player
      window.location.reload()
    }

    if (data.status === "failed") {
      window.location.reload()
    }
  }
}
